import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./ThemeProvider";

const PHASES = [
  {
    id: 1,
    badgeKey: "partner.dashboard.phase.one.badge",
    titleKey: "partner.dashboard.phase.one.title",
    subtitleKey: "partner.dashboard.phase.one.subtitle",
    timelineKey: "partner.dashboard.phase.one.timeline",
  },
  {
    id: 2,
    badgeKey: "partner.dashboard.phase.two.badge",
    titleKey: "partner.dashboard.phase.two.title",
    subtitleKey: "partner.dashboard.phase.two.subtitle",
    timelineKey: "partner.dashboard.phase.two.timeline",
  },
  {
    id: 3,
    badgeKey: "partner.dashboard.phase.three.badge",
    titleKey: "partner.dashboard.phase.three.title",
    subtitleKey: "partner.dashboard.phase.three.subtitle",
    timelineKey: "partner.dashboard.phase.three.timeline",
  },
];

// Deterministic fake data so a team-lead demo always shows the same numbers
// per user, and they look slightly personalised.
function buildFakePartnerData(partner, user, locale) {
  const id =
    Number(partner?.id) || Number(user?.id) || Math.floor(Math.random() * 999);
  const code = `NOMIO-${String(id).padStart(4, "0")}-AM`;
  const origin =
    typeof window !== "undefined" && window.location && window.location.origin
      ? window.location.origin
      : "https://app.nomio.am";

  const isHy = locale === "hy";
  const link = `${origin}/${locale || "hy"}/signup?ref=${code}`;

  return {
    code,
    link,
    commission: {
      ratePercent: 25,
      recurringMonths: 12,
      minPayout: 50000,
    },
    stats: {
      totalReferrals: 12 + (id % 5),
      activeClients: 7 + (id % 3),
      lifetimeEarnings: 437500 + (id % 7) * 12500,
      pendingPayout: 87500,
    },
    manager: {
      name: isHy ? "Արամ Մինասյան" : "Aram Minasyan",
      email: "partners@nomio.am",
      phone: "+374 99 33 44 55",
      telegram: "nomio_partner_support",
    },

    // === Phase 2 data ===
    referrals: [
      {
        id: 1,
        company: isHy ? "Ակբա Բանկ" : "Acba Bank",
        contact: "info@acba.am",
        status: "active",
        plan: isHy ? "Բիզնես" : "Business",
        mrr: 125000,
        joinedAt: "2026-03-14",
      },
      {
        id: 2,
        company: isHy ? "Արաբկիր պոլիկլինիկա" : "Arabkir Polyclinic",
        contact: "office@arabkir.am",
        status: "active",
        plan: isHy ? "Պրեմիում" : "Premium",
        mrr: 95000,
        joinedAt: "2026-04-02",
      },
      {
        id: 3,
        company: isHy ? "ՊԵԿ կենտրոն" : "Tax Service Center",
        contact: "ops@tax.am",
        status: "active",
        plan: isHy ? "Կորպորատիվ" : "Enterprise",
        mrr: 215000,
        joinedAt: "2026-04-21",
      },
      {
        id: 4,
        company: isHy ? "Yerevan Clinic" : "Yerevan Clinic",
        contact: "billing@yclinic.am",
        status: "trial",
        plan: isHy ? "Բիզնես" : "Business",
        mrr: 0,
        joinedAt: "2026-05-18",
      },
      {
        id: 5,
        company: isHy ? "Մեգաֆոն Մ․Մ․" : "Megaphone LLC",
        contact: "hr@megaphone.am",
        status: "paid",
        plan: isHy ? "Ստարտեր" : "Starter",
        mrr: 45000,
        joinedAt: "2026-05-29",
      },
      {
        id: 6,
        company: isHy ? "Արարատ Գրուպ" : "Ararat Group",
        contact: "info@ararat.am",
        status: "churned",
        plan: isHy ? "Բիզնես" : "Business",
        mrr: 0,
        joinedAt: "2026-01-08",
      },
    ],
    earningsMonthly: [
      { label: isHy ? "Հնվ" : "Jan", amount: 28000 },
      { label: isHy ? "Փտր" : "Feb", amount: 41500 },
      { label: isHy ? "Մրտ" : "Mar", amount: 67000 },
      { label: isHy ? "Ապր" : "Apr", amount: 82500 },
      { label: isHy ? "Մյս" : "May", amount: 109000 },
      { label: isHy ? "Հնս" : "Jun", amount: 87500 },
    ],
    payouts: [
      {
        date: "2026-05-15",
        amount: 109000,
        method: "bank_transfer",
        status: "paid",
        ref: "PYT-20260515",
      },
      {
        date: "2026-04-15",
        amount: 82500,
        method: "bank_transfer",
        status: "paid",
        ref: "PYT-20260415",
      },
      {
        date: "2026-03-15",
        amount: 67000,
        method: "bank_transfer",
        status: "paid",
        ref: "PYT-20260315",
      },
      {
        date: "2026-07-15",
        amount: 87500,
        method: "bank_transfer",
        status: "pending",
        ref: "PYT-20260715",
      },
    ],
    resources: [
      {
        key: "presentation",
        ext: "PDF",
        size: "2.4 MB",
      },
      {
        key: "logoPack",
        ext: "ZIP",
        size: "8.1 MB",
      },
      {
        key: "salesScript",
        ext: "DOCX",
        size: "210 KB",
      },
      {
        key: "demoVideo",
        ext: "MP4",
        size: "84 MB",
      },
      {
        key: "caseStudy",
        ext: "PDF",
        size: "1.6 MB",
      },
      {
        key: "emailTemplates",
        ext: "ZIP",
        size: "420 KB",
      },
    ],

    // === Phase 3 data ===
    tier: {
      currentKey: "silver",
      nextKey: "gold",
      currentRate: 25,
      nextRate: 30,
      currentDeals: 18,
      requiredDeals: 30,
    },
    deals: [
      {
        id: 1,
        prospect: isHy ? "Կոնվերս Բանկ" : "Converse Bank",
        contact: "tigran@converse.am",
        estValue: 1450000,
        registeredAt: "2026-05-20",
        expiresAt: "2026-08-20",
        status: "approved",
      },
      {
        id: 2,
        prospect: isHy ? "Մեդիցինալ կենտրոն Շենգավիթ" : "Shengavit Medical Center",
        contact: "admin@shengavit.am",
        estValue: 720000,
        registeredAt: "2026-05-29",
        expiresAt: "2026-08-29",
        status: "pending",
      },
      {
        id: 3,
        prospect: isHy ? "Էներգոնետ ՓԲԸ" : "Energonet CJSC",
        contact: "office@energonet.am",
        estValue: 980000,
        registeredAt: "2026-04-10",
        expiresAt: "2026-07-10",
        status: "expiring",
      },
    ],
    campaigns: [
      {
        name: isHy ? "Բանկեր Q2" : "Banks Q2",
        slug: "banks-q2",
        clicks: 142,
        conversions: 7,
      },
      {
        name: isHy ? "Բուժ․ կենտրոններ" : "Healthcare 2026",
        slug: "healthcare-2026",
        clicks: 89,
        conversions: 4,
      },
      {
        name: isHy ? "LinkedIn outbound" : "LinkedIn outbound",
        slug: "linkedin-out",
        clicks: 56,
        conversions: 2,
      },
    ],
    leaderboard: {
      partnerRank: 4,
      total: 47,
      top: [
        { rank: 1, name: isHy ? "Անի Բաղդասարյան" : "Ani Baghdasaryan", deals: 28 },
        { rank: 2, name: isHy ? "Դավիթ Հովհաննիսյան" : "Davit Hovhannisyan", deals: 24 },
        { rank: 3, name: isHy ? "Մարիամ Սարգսյան" : "Mariam Sargsyan", deals: 21 },
        { rank: 4, name: isHy ? "Դուք" : "You", deals: 18, isCurrent: true },
        { rank: 5, name: isHy ? "Տիգրան Ղազարյան" : "Tigran Ghazaryan", deals: 16 },
      ],
    },
  };
}

function formatNumber(n, locale) {
  try {
    return new Intl.NumberFormat(locale).format(n);
  } catch (e) {
    return String(n);
  }
}

function formatDate(iso, locale) {
  try {
    return new Date(iso).toLocaleDateString(
      locale === "hy" ? "hy-AM" : "en-US",
      { year: "numeric", month: "short", day: "2-digit" },
    );
  } catch (e) {
    return iso;
  }
}

function ApprovedPartnerDashboard({ partner, user }) {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  // CSS uses inverted convention: `[data-theme="light"]` triggers dark
  // visuals, so visual dark mode is when isDarkMode is FALSE.
  const isVisuallyDark = !isDarkMode;
  const lang = i18n.language || "hy";
  const numLocale = lang === "hy" ? "hy-AM" : "en-US";
  const currencySymbol = "֏";

  const data = useMemo(
    () => buildFakePartnerData(partner, user, lang),
    [partner, user, lang],
  );

  const [previewVisible, setPreviewVisible] = useState(true);
  const [phase, setPhase] = useState(1);
  const [copyState, setCopyState] = useState({ link: false, code: false });
  const phaseTwoRef = useRef(null);
  const phaseThreeRef = useRef(null);
  const phaseInitial = useRef(true);

  // Auto-scroll to the newly revealed phase header when user clicks Phase 2/3.
  useEffect(() => {
    if (phaseInitial.current) {
      phaseInitial.current = false;
      return;
    }
    const target =
      phase === 2 ? phaseTwoRef.current : phase === 3 ? phaseThreeRef.current : null;
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [phase]);

  const flashCopied = (type) => {
    setCopyState((s) => ({ ...s, [type]: true }));
    setTimeout(() => setCopyState((s) => ({ ...s, [type]: false })), 1800);
  };

  const fallbackCopy = (text, type) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      flashCopied(type);
    } catch (e) {
      /* demo: silent */
    }
  };

  const copy = (text, type) => {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      window.isSecureContext
    ) {
      navigator.clipboard.writeText(text).then(
        () => flashCopied(type),
        () => fallbackCopy(text, type),
      );
    } else {
      fallbackCopy(text, type);
    }
  };

  const managerInitials = data.manager.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => (p[0] || "").toUpperCase())
    .join("");

  return (
    <div className="approved-partner-dashboard d-flex flex-column gap-4 mb-4">
      {previewVisible && (
        <PreviewNotice
          isVisuallyDark={isVisuallyDark}
          onHide={() => setPreviewVisible(false)}
          title={t("partner.dashboard.preview.title")}
          description={t("partner.dashboard.preview.description")}
          hideLabel={t("partner.dashboard.preview.hide")}
        />
      )}

      <PhaseSelector phase={phase} setPhase={setPhase} t={t} />

      {/* === Phase 1 === */}
      <PhaseHeader phase={PHASES[0]} t={t} />

      <ReferralCard
        code={data.code}
        link={data.link}
        copyState={copyState}
        onCopy={copy}
        t={t}
      />

      <StatsGrid
        stats={data.stats}
        numLocale={numLocale}
        currencySymbol={currencySymbol}
        t={t}
      />

      <div className="row gy-4">
        <div className="col-lg-6 d-flex">
          <CommissionCard
            commission={data.commission}
            numLocale={numLocale}
            currencySymbol={currencySymbol}
            t={t}
          />
        </div>
        <div className="col-lg-6 d-flex">
          <ManagerCard
            manager={data.manager}
            managerInitials={managerInitials}
            t={t}
          />
        </div>
      </div>

      {/* === Phase 2 === */}
      {phase >= 2 && (
        <>
          <div ref={phaseTwoRef} />
          <PhaseHeader phase={PHASES[1]} t={t} />

          <ReferralsListCard
            referrals={data.referrals}
            numLocale={numLocale}
            currencySymbol={currencySymbol}
            lang={lang}
            t={t}
          />

          <div className="row gy-4">
            <div className="col-xl-7 d-flex">
              <EarningsChartCard
                series={data.earningsMonthly}
                numLocale={numLocale}
                currencySymbol={currencySymbol}
                t={t}
              />
            </div>
            <div className="col-xl-5 d-flex">
              <PayoutHistoryCard
                payouts={data.payouts}
                numLocale={numLocale}
                currencySymbol={currencySymbol}
                lang={lang}
                t={t}
              />
            </div>
          </div>

          <MarketingResourcesCard resources={data.resources} t={t} />
        </>
      )}

      {/* === Phase 3 === */}
      {phase >= 3 && (
        <>
          <div ref={phaseThreeRef} />
          <PhaseHeader phase={PHASES[2]} t={t} />

          <TierProgressionCard tier={data.tier} t={t} />

          <div className="row gy-4">
            <div className="col-xl-7 d-flex">
              <DealRegistrationCard
                deals={data.deals}
                numLocale={numLocale}
                currencySymbol={currencySymbol}
                lang={lang}
                t={t}
              />
            </div>
            <div className="col-xl-5 d-flex">
              <QrCodeCard
                code={data.code}
                link={data.link}
                t={t}
              />
            </div>
          </div>

          <div className="row gy-4">
            <div className="col-lg-7 d-flex">
              <MultiCampaignCard
                campaigns={data.campaigns}
                partnerCode={data.code}
                numLocale={numLocale}
                t={t}
              />
            </div>
            <div className="col-lg-5 d-flex">
              <LeaderboardCard
                leaderboard={data.leaderboard}
                t={t}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* =====================================================================
 * Shared
 * ===================================================================== */

function PreviewNotice({ isVisuallyDark, onHide, title, description, hideLabel }) {
  // Same theme-aware palette used by the profile-level preview banners
  // so the demo styling stays consistent across the page.
  const bg = isVisuallyDark
    ? "linear-gradient(135deg, hsl(38 100% 58% / 0.22), hsl(38 100% 58% / 0.10))"
    : "hsl(38 92% 50% / 0.12)";
  const border = isVisuallyDark
    ? "1px dashed hsl(38 100% 68%)"
    : "1px dashed hsl(38 92% 45%)";
  const headingColor = isVisuallyDark ? "hsl(38 100% 85%)" : "hsl(38 92% 22%)";
  const bodyColor = isVisuallyDark ? "hsl(38 100% 78%)" : "hsl(38 92% 28%)";
  const iconBg = isVisuallyDark
    ? "hsl(38 100% 60% / 0.24)"
    : "hsl(38 92% 50% / 0.18)";
  const iconColor = isVisuallyDark ? "hsl(38 100% 72%)" : "hsl(38 92% 35%)";
  const ctaBg = isVisuallyDark
    ? "linear-gradient(135deg, hsl(38 100% 60%), hsl(28 100% 52%))"
    : "hsl(38 92% 45%)";
  const ctaText = isVisuallyDark ? "hsl(28 60% 14%)" : "#fff";
  const ctaShadow = isVisuallyDark
    ? "0 8px 24px hsl(38 100% 50% / 0.32)"
    : "0 4px 12px hsl(38 92% 50% / 0.28)";
  const glow = isVisuallyDark
    ? "0 0 0 1px hsl(38 100% 60% / 0.08), 0 8px 24px hsl(38 100% 50% / 0.10)"
    : "none";

  return (
    <div
      className="rounded-3 p-3 d-flex flex-wrap align-items-start justify-content-between gap-3"
      style={{
        background: bg,
        border,
        color: headingColor,
        boxShadow: glow,
      }}
    >
      <div className="d-flex align-items-start gap-2 flex-grow-1">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{
            width: 36,
            height: 36,
            backgroundColor: iconBg,
            color: iconColor,
          }}
          aria-hidden="true"
        >
          <i className="las la-flask" style={{ fontSize: "1.3rem" }} />
        </span>
        <div>
          <div className="fw-700 font-14" style={{ color: headingColor }}>
            {title}
          </div>
          <div className="font-13" style={{ color: bodyColor, opacity: 0.95 }}>
            {description}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onHide}
        className="btn btn-sm pill fw-600 px-3 flex-shrink-0"
        style={{
          background: ctaBg,
          color: ctaText,
          border: "none",
          boxShadow: ctaShadow,
        }}
      >
        <i className="las la-eye-slash me-1" />
        {hideLabel}
      </button>
    </div>
  );
}

function PhaseSelector({ phase, setPhase, t }) {
  return (
    <div
      className="rounded-4 shadow-sm p-3 d-flex flex-wrap align-items-center justify-content-between gap-3"
      style={{
        background:
          "linear-gradient(135deg, hsl(var(--main) / 0.08) 0%, hsl(var(--main) / 0.02) 100%)",
        border: "1px solid hsl(var(--main) / 0.18)",
      }}
    >
      <div>
        <div
          className="font-12 text-uppercase fw-700"
          style={{ letterSpacing: "0.08em", color: "hsl(var(--main))" }}
        >
          {t("partner.dashboard.phaseSelector.label")}
        </div>
        <div className="font-14 text-body">
          {t("partner.dashboard.phaseSelector.hint")}
        </div>
      </div>
      <div className="d-flex flex-wrap gap-2" role="tablist">
        {PHASES.map((p) => {
          const active = phase === p.id;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setPhase(p.id)}
              className="btn pill fw-600 px-3 py-2"
              style={
                active
                  ? {
                      background: "var(--main-gradient)",
                      color: "#fff",
                      border: "none",
                      boxShadow: "0 8px 24px hsl(var(--main) / 0.28)",
                    }
                  : {
                      backgroundColor: "transparent",
                      color: "hsl(var(--main))",
                      border: "1px solid hsl(var(--main) / 0.4)",
                    }
              }
            >
              <span className="d-inline-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-circle fw-700"
                  style={{
                    width: 22,
                    height: 22,
                    fontSize: 11,
                    backgroundColor: active
                      ? "rgba(255, 255, 255, 0.22)"
                      : "hsl(var(--main) / 0.12)",
                  }}
                >
                  {p.id}
                </span>
                <span className="font-13">{t(p.badgeKey)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PhaseHeader({ phase, t }) {
  return (
    <div
      className="rounded-3 p-3 d-flex flex-wrap align-items-center justify-content-between gap-3"
      style={{
        backgroundColor: "hsl(var(--main) / 0.08)",
        border: "1px solid hsl(var(--main) / 0.18)",
      }}
    >
      <div className="d-flex align-items-center gap-3">
        <span
          className="d-inline-flex align-items-center justify-content-center text-white fw-700"
          style={{
            width: 44,
            height: 44,
            background: "var(--main-gradient)",
            borderRadius: 12,
            fontSize: 18,
          }}
        >
          {phase.id}
        </span>
        <div>
          <div
            className="font-11 text-uppercase fw-700"
            style={{ letterSpacing: "0.08em", color: "hsl(var(--main))" }}
          >
            {t(phase.badgeKey)} · {t(phase.timelineKey)}
          </div>
          <div className="font-18 fw-600">{t(phase.titleKey)}</div>
        </div>
      </div>
      <div
        className="font-13 text-body"
        style={{ maxWidth: 540, textAlign: "right" }}
      >
        {t(phase.subtitleKey)}
      </div>
    </div>
  );
}

/* =====================================================================
 * Phase 1
 * ===================================================================== */

function ReferralCard({ code, link, copyState, onCopy, t }) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4">
      <SectionHeader
        icon="la-link"
        gradient
        title={t("partner.dashboard.referral.title")}
        subtitle={t("partner.dashboard.referral.subtitle")}
      />
      <div className="row gy-3 mt-1">
        <div className="col-12 col-md-5">
          <Label text={t("partner.dashboard.referral.codeLabel")} />
          <div className="d-flex gap-2 flex-wrap flex-sm-nowrap">
            <div
              className="flex-grow-1 d-flex align-items-center px-3 rounded-3 fw-600 font-15"
              style={{
                minHeight: 44,
                backgroundColor: "hsl(var(--main) / 0.10)",
                color: "hsl(var(--main))",
                letterSpacing: "0.05em",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={code}
            >
              {code}
            </div>
            <button
              type="button"
              onClick={() => onCopy(code, "code")}
              className="btn btn-outline-main pill fw-500 px-3"
              style={{ minHeight: 44, flexShrink: 0 }}
            >
              <i
                className={`las ${copyState.code ? "la-check" : "la-copy"} me-1`}
              />
              {copyState.code
                ? t("partner.dashboard.referral.copied")
                : t("partner.dashboard.referral.copy")}
            </button>
          </div>
        </div>
        <div className="col-12 col-md-7">
          <Label text={t("partner.dashboard.referral.linkLabel")} />
          <div className="d-flex gap-2 flex-wrap flex-sm-nowrap">
            <input
              type="text"
              readOnly
              value={link}
              onFocus={(e) => e.target.select()}
              onClick={(e) => e.target.select()}
              className="flex-grow-1 px-3 rounded-3 font-14"
              style={{
                minHeight: 44,
                border: "1px solid hsl(var(--border-color, 220 13% 91%))",
                backgroundColor: "hsl(var(--white) / 1)",
                color: "hsl(var(--heading-color))",
                minWidth: 0,
              }}
            />
            <button
              type="button"
              onClick={() => onCopy(link, "link")}
              className="btn btn-main pill fw-500 px-3"
              style={{ minHeight: 44, flexShrink: 0 }}
            >
              <i
                className={`las ${copyState.link ? "la-check" : "la-copy"} me-1`}
              />
              {copyState.link
                ? t("partner.dashboard.referral.copied")
                : t("partner.dashboard.referral.copyLink")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsGrid({ stats, numLocale, currencySymbol, t }) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <h5 className="mb-0 font-18 fw-600">
          {t("partner.dashboard.stats.title")}
        </h5>
        <span className="font-13 text-body">
          <i className="las la-info-circle me-1" />
          {t("partner.dashboard.stats.hint")}
        </span>
      </div>
      <div className="row gy-3">
        <StatCard
          icon="la-user-friends"
          label={t("partner.dashboard.stats.totalReferrals")}
          value={formatNumber(stats.totalReferrals, numLocale)}
          hint={t("partner.dashboard.stats.totalReferralsHint")}
          tone="info"
        />
        <StatCard
          icon="la-user-check"
          label={t("partner.dashboard.stats.activeClients")}
          value={formatNumber(stats.activeClients, numLocale)}
          hint={t("partner.dashboard.stats.activeClientsHint")}
          tone="success"
        />
        <StatCard
          icon="la-coins"
          label={t("partner.dashboard.stats.lifetimeEarnings")}
          value={`${formatNumber(
            stats.lifetimeEarnings,
            numLocale,
          )} ${currencySymbol}`}
          hint={t("partner.dashboard.stats.lifetimeEarningsHint")}
          tone="main"
        />
        <StatCard
          icon="la-hourglass-half"
          label={t("partner.dashboard.stats.pendingPayout")}
          value={`${formatNumber(
            stats.pendingPayout,
            numLocale,
          )} ${currencySymbol}`}
          hint={t("partner.dashboard.stats.pendingPayoutHint")}
          tone="warning"
        />
      </div>
    </div>
  );
}

function CommissionCard({ commission, numLocale, currencySymbol, t }) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4 w-100 d-flex flex-column">
      <SectionHeader
        icon="la-percentage"
        title={t("partner.dashboard.commission.title")}
        subtitle={t("partner.dashboard.commission.subtitle")}
      />
      <ul className="list-unstyled mb-0 d-flex flex-column mt-2">
        <CommissionRow
          label={t("partner.dashboard.commission.rate")}
          value={`${commission.ratePercent}%`}
          accent
        />
        <CommissionRow
          label={t("partner.dashboard.commission.recurring")}
          value={t("partner.dashboard.commission.recurringValue", {
            months: commission.recurringMonths,
          })}
        />
        <CommissionRow
          label={t("partner.dashboard.commission.payoutPeriod")}
          value={t("partner.dashboard.commission.payoutMonthly")}
        />
        <CommissionRow
          label={t("partner.dashboard.commission.payoutMethod")}
          value={t("partner.dashboard.commission.payoutBank")}
        />
        <CommissionRow
          label={t("partner.dashboard.commission.minPayout")}
          value={`${formatNumber(
            commission.minPayout,
            numLocale,
          )} ${currencySymbol}`}
        />
      </ul>
    </div>
  );
}

function ManagerCard({ manager, managerInitials, t }) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4 w-100 d-flex flex-column">
      <SectionHeader
        icon="la-headset"
        title={t("partner.dashboard.manager.title")}
        subtitle={t("partner.dashboard.manager.subtitle")}
      />
      <div className="d-flex flex-column gap-3 mt-2">
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle text-white d-inline-flex align-items-center justify-content-center fw-700 flex-shrink-0"
            style={{
              width: 48,
              height: 48,
              fontSize: 18,
              background: "var(--main-gradient)",
            }}
          >
            {managerInitials || "AM"}
          </div>
          <div>
            <div className="fw-600 font-16">{manager.name}</div>
            <div className="font-13 text-body">
              {t("partner.dashboard.manager.role")}
            </div>
          </div>
        </div>
        <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
          <ManagerLink
            iconClass="las la-envelope"
            label={t("partner.dashboard.manager.emailLabel")}
            value={manager.email}
            href={`mailto:${manager.email}`}
          />
          <ManagerLink
            iconClass="las la-phone"
            label={t("partner.dashboard.manager.phoneLabel")}
            value={manager.phone}
            href={`tel:${manager.phone.replace(/\s+/g, "")}`}
          />
          <ManagerLink
            iconClass="lab la-telegram-plane"
            label={t("partner.dashboard.manager.telegramLabel")}
            value={`@${manager.telegram}`}
            href={`https://t.me/${manager.telegram}`}
            external
          />
        </ul>
      </div>
    </div>
  );
}

/* =====================================================================
 * Phase 2
 * ===================================================================== */

const REFERRAL_STATUS_STYLES = {
  active: { bg: "hsl(150 60% 45% / 0.14)", fg: "hsl(150 60% 30%)" },
  paid: { bg: "hsl(212 95% 50% / 0.12)", fg: "hsl(212 95% 42%)" },
  trial: { bg: "hsl(38 92% 50% / 0.16)", fg: "hsl(38 92% 35%)" },
  churned: { bg: "hsl(0 0% 50% / 0.18)", fg: "hsl(0 0% 35%)" },
};

function ReferralsListCard({
  referrals,
  numLocale,
  currencySymbol,
  lang,
  t,
}) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <SectionHeader
          icon="la-users"
          title={t("partner.dashboard.referrals.title")}
          subtitle={t("partner.dashboard.referrals.subtitle")}
        />
        <div className="d-flex gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn btn-sm btn-outline-main pill px-3"
            disabled
          >
            <i className="las la-filter me-1" />
            {t("partner.dashboard.referrals.filter")}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-main pill px-3"
            disabled
          >
            <i className="las la-download me-1" />
            {t("partner.dashboard.referrals.export")}
          </button>
        </div>
      </div>

      <div className="table-responsive" style={{ overflowX: "auto" }}>
        <table className="table align-middle mb-0" style={{ minWidth: 720 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid hsl(var(--border-color, 220 13% 91%))" }}>
              <Th>{t("partner.dashboard.referrals.col.company")}</Th>
              <Th>{t("partner.dashboard.referrals.col.status")}</Th>
              <Th>{t("partner.dashboard.referrals.col.plan")}</Th>
              <Th align="right">{t("partner.dashboard.referrals.col.mrr")}</Th>
              <Th>{t("partner.dashboard.referrals.col.joined")}</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {referrals.map((r) => {
              const style =
                REFERRAL_STATUS_STYLES[r.status] || REFERRAL_STATUS_STYLES.trial;
              return (
                <tr
                  key={r.id}
                  style={{
                    borderBottom:
                      "1px dashed hsl(var(--border-color, 220 13% 91%) / 0.7)",
                  }}
                >
                  <Td>
                    <div className="fw-600">{r.company}</div>
                    <div className="font-12 text-body">{r.contact}</div>
                  </Td>
                  <Td>
                    <span
                      className="badge fw-600 rounded-pill px-3 py-2"
                      style={{
                        backgroundColor: style.bg,
                        color: style.fg,
                      }}
                    >
                      {t(`partner.dashboard.referrals.status.${r.status}`)}
                    </span>
                  </Td>
                  <Td>{r.plan}</Td>
                  <Td align="right">
                    {r.mrr > 0 ? (
                      <span className="fw-600">
                        {formatNumber(r.mrr, numLocale)} {currencySymbol}
                      </span>
                    ) : (
                      <span className="text-body">—</span>
                    )}
                  </Td>
                  <Td>{formatDate(r.joinedAt, lang)}</Td>
                  <Td align="right">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-main rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: 32, height: 32 }}
                      aria-label="view"
                    >
                      <i className="las la-ellipsis-h" />
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EarningsChartCard({ series, numLocale, currencySymbol, t }) {
  const total = series.reduce((acc, s) => acc + s.amount, 0);
  const last = series[series.length - 1]?.amount || 0;
  const prev = series[series.length - 2]?.amount || 0;
  const growth = prev > 0 ? Math.round(((last - prev) / prev) * 100) : 0;

  return (
    <div className="bg-white rounded-4 shadow-sm p-4 w-100 d-flex flex-column">
      <SectionHeader
        icon="la-chart-bar"
        title={t("partner.dashboard.earnings.title")}
        subtitle={t("partner.dashboard.earnings.subtitle")}
      />

      <div className="d-flex flex-wrap align-items-end gap-3 mt-2 mb-2">
        <div>
          <div className="font-12 text-uppercase fw-700 text-body" style={{ letterSpacing: "0.05em" }}>
            {t("partner.dashboard.earnings.total")}
          </div>
          <div
            className="font-22 fw-700"
            style={{ color: "hsl(var(--heading-color))" }}
          >
            {formatNumber(total, numLocale)} {currencySymbol}
          </div>
        </div>
        <div>
          <span
            className="badge fw-600 rounded-pill px-3 py-2"
            style={{
              backgroundColor:
                growth >= 0
                  ? "hsl(150 60% 45% / 0.14)"
                  : "hsl(0 80% 50% / 0.14)",
              color: growth >= 0 ? "hsl(150 60% 30%)" : "hsl(0 80% 40%)",
            }}
          >
            <i
              className={`las ${
                growth >= 0 ? "la-arrow-up" : "la-arrow-down"
              } me-1`}
            />
            {growth >= 0 ? "+" : ""}
            {growth}% {t("partner.dashboard.earnings.vsPrev")}
          </span>
        </div>
      </div>

      <MiniBarChart series={series} numLocale={numLocale} currencySymbol={currencySymbol} />
    </div>
  );
}

function MiniBarChart({ series, numLocale, currencySymbol }) {
  const max = Math.max(...series.map((s) => s.amount), 1);
  const height = 180;
  const barWidth = 32;
  const barGap = 18;
  const padTop = 14;
  const padBottom = 32;
  const chartHeight = height - padTop - padBottom;
  const width = series.length * (barWidth + barGap);

  return (
    <div className="position-relative" style={{ overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        style={{ minWidth: width, overflow: "visible" }}
      >
        <defs>
          <linearGradient id="bar-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--main))" stopOpacity="0.95" />
            <stop offset="100%" stopColor="hsl(var(--main))" stopOpacity="0.45" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line
            key={g}
            x1="0"
            x2={width}
            y1={padTop + chartHeight * (1 - g)}
            y2={padTop + chartHeight * (1 - g)}
            stroke="hsl(var(--border-color, 220 13% 91%))"
            strokeDasharray="3,4"
            strokeWidth="1"
            opacity="0.6"
          />
        ))}

        {series.map((s, i) => {
          const h = (s.amount / max) * chartHeight;
          const x = i * (barWidth + barGap);
          const y = padTop + chartHeight - h;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={h}
                rx={6}
                fill="url(#bar-gradient)"
              />
              <text
                x={x + barWidth / 2}
                y={y - 6}
                textAnchor="middle"
                fontSize="10"
                fontWeight="600"
                fill="hsl(var(--heading-color))"
              >
                {formatNumber(Math.round(s.amount / 1000), numLocale)}k
              </text>
              <text
                x={x + barWidth / 2}
                y={padTop + chartHeight + 18}
                textAnchor="middle"
                fontSize="11"
                fill="hsl(var(--body-color))"
              >
                {s.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function PayoutHistoryCard({ payouts, numLocale, currencySymbol, lang, t }) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4 w-100 d-flex flex-column">
      <SectionHeader
        icon="la-wallet"
        title={t("partner.dashboard.payouts.title")}
        subtitle={t("partner.dashboard.payouts.subtitle")}
      />
      <ul className="list-unstyled mb-0 d-flex flex-column gap-2 mt-2">
        {payouts.map((p) => {
          const isPending = p.status === "pending";
          return (
            <li
              key={p.ref}
              className="d-flex align-items-center justify-content-between gap-3 rounded-3 p-3"
              style={{
                backgroundColor: isPending
                  ? "hsl(38 92% 50% / 0.08)"
                  : "hsl(var(--section-bg, 210 14% 97%) / 0.6)",
                border: isPending
                  ? "1px solid hsl(38 92% 50% / 0.32)"
                  : "1px solid hsl(var(--border-color, 220 13% 91%) / 0.6)",
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{
                    width: 36,
                    height: 36,
                    backgroundColor: isPending
                      ? "hsl(38 92% 50% / 0.22)"
                      : "hsl(150 60% 45% / 0.18)",
                    color: isPending
                      ? "hsl(38 92% 35%)"
                      : "hsl(150 60% 30%)",
                  }}
                  aria-hidden="true"
                >
                  <i
                    className={`las ${
                      isPending ? "la-hourglass-half" : "la-check-circle"
                    }`}
                    style={{ fontSize: "1.2rem" }}
                  />
                </span>
                <div>
                  <div className="fw-600 font-14">
                    {formatNumber(p.amount, numLocale)} {currencySymbol}
                  </div>
                  <div className="font-12 text-body">
                    {formatDate(p.date, lang)} · {p.ref}
                  </div>
                </div>
              </div>
              <span
                className="badge fw-600 rounded-pill px-3 py-2"
                style={{
                  backgroundColor: isPending
                    ? "hsl(38 92% 50% / 0.18)"
                    : "hsl(150 60% 45% / 0.16)",
                  color: isPending
                    ? "hsl(38 92% 35%)"
                    : "hsl(150 60% 30%)",
                }}
              >
                {t(`partner.dashboard.payouts.status.${p.status}`)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function MarketingResourcesCard({ resources, t }) {
  const icons = {
    presentation: "la-file-powerpoint",
    logoPack: "la-images",
    salesScript: "la-file-word",
    demoVideo: "la-video",
    caseStudy: "la-file-pdf",
    emailTemplates: "la-envelope-open-text",
  };
  return (
    <div className="bg-white rounded-4 shadow-sm p-4">
      <SectionHeader
        icon="la-tools"
        title={t("partner.dashboard.resources.title")}
        subtitle={t("partner.dashboard.resources.subtitle")}
      />
      <div className="row gy-3 mt-1">
        {resources.map((r) => (
          <div key={r.key} className="col-md-6 col-lg-4">
            <button
              type="button"
              className="w-100 text-start rounded-3 p-3 d-flex align-items-center gap-3 bg-transparent"
              style={{
                border: "1px solid hsl(var(--border-color, 220 13% 91%))",
                color: "hsl(var(--heading-color))",
                transition: "border-color 0.2s, background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "hsl(var(--main) / 0.5)";
                e.currentTarget.style.backgroundColor = "hsl(var(--main) / 0.04)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor =
                  "hsl(var(--border-color, 220 13% 91%))";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  backgroundColor: "hsl(var(--main) / 0.12)",
                  color: "hsl(var(--main))",
                }}
              >
                <i
                  className={`las ${icons[r.key] || "la-file"}`}
                  style={{ fontSize: "1.4rem" }}
                />
              </span>
              <div className="flex-grow-1 min-width-0">
                <div className="fw-600 font-14 text-truncate">
                  {t(`partner.dashboard.resources.items.${r.key}.title`)}
                </div>
                <div className="font-12 text-body">
                  {r.ext} · {r.size}
                </div>
              </div>
              <i
                className="las la-download flex-shrink-0"
                style={{ fontSize: "1.2rem", color: "hsl(var(--main))" }}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =====================================================================
 * Phase 3
 * ===================================================================== */

const TIER_PALETTE = {
  bronze: { fg: "#7c4a1d", bg: "linear-gradient(135deg, #c98e5b, #a06436)" },
  silver: { fg: "#3a3f47", bg: "linear-gradient(135deg, #d8dde6, #a4abb6)" },
  gold: { fg: "#6b4a08", bg: "linear-gradient(135deg, #ffd86b, #c89200)" },
  platinum: { fg: "#1e2a44", bg: "linear-gradient(135deg, #b3d4ff, #7ba7e0)" },
};

function TierProgressionCard({ tier, t }) {
  const progress = Math.min(1, tier.currentDeals / tier.requiredDeals);
  const remaining = Math.max(0, tier.requiredDeals - tier.currentDeals);
  const currentPalette = TIER_PALETTE[tier.currentKey] || TIER_PALETTE.silver;
  const nextPalette = TIER_PALETTE[tier.nextKey] || TIER_PALETTE.gold;

  return (
    <div className="bg-white rounded-4 shadow-sm p-4">
      <SectionHeader
        icon="la-medal"
        title={t("partner.dashboard.tier.title")}
        subtitle={t("partner.dashboard.tier.subtitle")}
      />

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-inline-flex align-items-center justify-content-center text-white fw-700"
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              background: currentPalette.bg,
              fontSize: 24,
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            }}
            aria-hidden="true"
          >
            <i className="las la-medal" />
          </div>
          <div>
            <div className="font-12 text-uppercase fw-700 text-body" style={{ letterSpacing: "0.06em" }}>
              {t("partner.dashboard.tier.currentLabel")}
            </div>
            <div
              className="font-22 fw-700"
              style={{ color: "hsl(var(--heading-color))" }}
            >
              {t(`partner.dashboard.tier.levels.${tier.currentKey}`)}
            </div>
            <div className="font-13 text-body">
              {t("partner.dashboard.tier.currentRate", {
                percent: tier.currentRate,
              })}
            </div>
          </div>
        </div>

        <div
          className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
          style={{
            background: nextPalette.bg,
            color: "#fff",
            fontWeight: 600,
          }}
        >
          <i className="las la-arrow-up" />
          {t("partner.dashboard.tier.nextLabel", {
            tier: t(`partner.dashboard.tier.levels.${tier.nextKey}`),
            percent: tier.nextRate,
          })}
        </div>
      </div>

      <div className="mt-4">
        <div className="d-flex justify-content-between mb-2">
          <span className="font-13 text-body">
            {t("partner.dashboard.tier.progressLabel", {
              current: tier.currentDeals,
              required: tier.requiredDeals,
            })}
          </span>
          <span className="font-13 fw-600" style={{ color: "hsl(var(--main))" }}>
            {Math.round(progress * 100)}%
          </span>
        </div>
        <div
          style={{
            height: 12,
            borderRadius: 99,
            backgroundColor: "hsl(var(--section-bg, 210 14% 97%))",
            border: "1px solid hsl(var(--border-color, 220 13% 91%))",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: `${progress * 100}%`,
              height: "100%",
              background: "var(--main-gradient)",
              borderRadius: 99,
              transition: "width 0.6s ease",
            }}
          />
        </div>
        <div className="font-12 text-body mt-2">
          <i className="las la-info-circle me-1" />
          {t("partner.dashboard.tier.remaining", {
            count: remaining,
            tier: t(`partner.dashboard.tier.levels.${tier.nextKey}`),
            percent: tier.nextRate,
          })}
        </div>
      </div>
    </div>
  );
}

const DEAL_STATUS_STYLES = {
  approved: { bg: "hsl(150 60% 45% / 0.14)", fg: "hsl(150 60% 30%)" },
  pending: { bg: "hsl(38 92% 50% / 0.16)", fg: "hsl(38 92% 35%)" },
  expiring: { bg: "hsl(0 80% 50% / 0.14)", fg: "hsl(0 80% 42%)" },
};

function DealRegistrationCard({ deals, numLocale, currencySymbol, lang, t }) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4 w-100 d-flex flex-column">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <SectionHeader
          icon="la-handshake"
          title={t("partner.dashboard.deals.title")}
          subtitle={t("partner.dashboard.deals.subtitle")}
        />
        <button
          type="button"
          className="btn btn-main pill fw-500 px-3 flex-shrink-0"
        >
          <i className="las la-plus me-1" />
          {t("partner.dashboard.deals.registerBtn")}
        </button>
      </div>

      <ul className="list-unstyled mb-0 d-flex flex-column gap-2 mt-1">
        {deals.map((d) => {
          const style = DEAL_STATUS_STYLES[d.status] || DEAL_STATUS_STYLES.pending;
          return (
            <li
              key={d.id}
              className="rounded-3 p-3 d-flex flex-wrap align-items-center justify-content-between gap-3"
              style={{
                backgroundColor: "hsl(var(--section-bg, 210 14% 97%) / 0.6)",
                border:
                  "1px solid hsl(var(--border-color, 220 13% 91%) / 0.7)",
              }}
            >
              <div className="d-flex flex-column gap-1 min-width-0">
                <div className="fw-600 font-15 text-truncate">{d.prospect}</div>
                <div className="font-12 text-body">
                  <i className="las la-envelope me-1" />
                  {d.contact}
                </div>
                <div className="font-12 text-body">
                  <i className="las la-calendar me-1" />
                  {t("partner.dashboard.deals.expiresOn", {
                    date: formatDate(d.expiresAt, lang),
                  })}
                </div>
              </div>
              <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                <div
                  className="fw-700 font-15"
                  style={{ color: "hsl(var(--main))" }}
                >
                  {formatNumber(d.estValue, numLocale)} {currencySymbol}
                </div>
                <span
                  className="badge fw-600 rounded-pill px-3 py-2"
                  style={{ backgroundColor: style.bg, color: style.fg }}
                >
                  {t(`partner.dashboard.deals.status.${d.status}`)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <div
        className="mt-3 p-3 rounded-3 font-12"
        style={{
          backgroundColor: "hsl(212 95% 50% / 0.08)",
          border: "1px solid hsl(212 95% 50% / 0.22)",
          color: "hsl(212 95% 35%)",
        }}
      >
        <i className="las la-shield-alt me-1" />
        {t("partner.dashboard.deals.protectionHint")}
      </div>
    </div>
  );
}

function QrCodeCard({ code, link, t }) {
  // Public free QR generator — fine for a fake-data preview.
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(
    link,
  )}`;
  return (
    <div className="bg-white rounded-4 shadow-sm p-4 w-100 d-flex flex-column align-items-center text-center">
      <SectionHeader
        icon="la-qrcode"
        title={t("partner.dashboard.qr.title")}
        subtitle={t("partner.dashboard.qr.subtitle")}
        align="center"
      />
      <div
        className="rounded-3 p-3 d-inline-flex align-items-center justify-content-center mt-2"
        style={{
          border: "1px solid hsl(var(--border-color, 220 13% 91%))",
          backgroundColor: "#ffffff",
        }}
      >
        <img
          src={qrUrl}
          alt={`QR code for ${code}`}
          width={200}
          height={200}
          style={{ display: "block" }}
        />
      </div>
      <div
        className="font-13 fw-600 mt-3"
        style={{ color: "hsl(var(--main))", letterSpacing: "0.04em" }}
      >
        {code}
      </div>
      <div className="font-12 text-body mt-1 px-3">
        {t("partner.dashboard.qr.hint")}
      </div>
      <a
        href={qrUrl}
        download={`${code}-qr.png`}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-outline-main pill fw-500 px-3 mt-3"
      >
        <i className="las la-download me-1" />
        {t("partner.dashboard.qr.downloadBtn")}
      </a>
    </div>
  );
}

function MultiCampaignCard({ campaigns, partnerCode, numLocale, t }) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4 w-100 d-flex flex-column">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <SectionHeader
          icon="la-bullseye"
          title={t("partner.dashboard.campaigns.title")}
          subtitle={t("partner.dashboard.campaigns.subtitle")}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline-main pill px-3 flex-shrink-0"
        >
          <i className="las la-plus me-1" />
          {t("partner.dashboard.campaigns.createBtn")}
        </button>
      </div>

      <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
        {campaigns.map((c) => {
          const rate =
            c.clicks > 0 ? Math.round((c.conversions / c.clicks) * 100) : 0;
          return (
            <li
              key={c.slug}
              className="rounded-3 p-3"
              style={{
                backgroundColor: "hsl(var(--section-bg, 210 14% 97%) / 0.6)",
                border:
                  "1px solid hsl(var(--border-color, 220 13% 91%) / 0.7)",
              }}
            >
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="d-flex flex-column gap-1 min-width-0">
                  <div className="fw-600 font-14">{c.name}</div>
                  <div
                    className="font-12 text-body text-truncate"
                    style={{ maxWidth: 340 }}
                  >
                    <code
                      style={{
                        color: "hsl(var(--main))",
                        background: "transparent",
                        padding: 0,
                      }}
                    >
                      ?ref={partnerCode}&campaign={c.slug}
                    </code>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-shrink-0">
                  <div className="text-end">
                    <div className="font-11 text-uppercase fw-700 text-body" style={{ letterSpacing: "0.04em" }}>
                      {t("partner.dashboard.campaigns.clicks")}
                    </div>
                    <div className="fw-700 font-15">
                      {formatNumber(c.clicks, numLocale)}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="font-11 text-uppercase fw-700 text-body" style={{ letterSpacing: "0.04em" }}>
                      {t("partner.dashboard.campaigns.conversions")}
                    </div>
                    <div
                      className="fw-700 font-15"
                      style={{ color: "hsl(var(--main))" }}
                    >
                      {formatNumber(c.conversions, numLocale)}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="font-11 text-uppercase fw-700 text-body" style={{ letterSpacing: "0.04em" }}>
                      {t("partner.dashboard.campaigns.rate")}
                    </div>
                    <div className="fw-700 font-15">{rate}%</div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function LeaderboardCard({ leaderboard, t }) {
  return (
    <div className="bg-white rounded-4 shadow-sm p-4 w-100 d-flex flex-column">
      <SectionHeader
        icon="la-trophy"
        title={t("partner.dashboard.leaderboard.title")}
        subtitle={t("partner.dashboard.leaderboard.subtitle")}
      />
      <div
        className="font-13 text-body mt-2"
        style={{ marginBottom: 8 }}
      >
        {t("partner.dashboard.leaderboard.yourRank", {
          rank: leaderboard.partnerRank,
          total: leaderboard.total,
        })}
      </div>
      <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
        {leaderboard.top.map((row) => {
          const isCurrent = row.isCurrent;
          const medalColors = {
            1: "linear-gradient(135deg, #ffd86b, #c89200)",
            2: "linear-gradient(135deg, #d8dde6, #a4abb6)",
            3: "linear-gradient(135deg, #c98e5b, #a06436)",
          };
          return (
            <li
              key={row.rank}
              className="rounded-3 p-2 d-flex align-items-center gap-3"
              style={{
                backgroundColor: isCurrent
                  ? "hsl(var(--main) / 0.10)"
                  : "hsl(var(--section-bg, 210 14% 97%) / 0.6)",
                border: isCurrent
                  ? "1px solid hsl(var(--main) / 0.32)"
                  : "1px solid transparent",
              }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center text-white fw-700 flex-shrink-0"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  fontSize: 13,
                  background:
                    medalColors[row.rank] ||
                    (isCurrent
                      ? "var(--main-gradient)"
                      : "hsl(var(--border-color, 220 13% 91%))"),
                  color: row.rank <= 3 || isCurrent ? "#fff" : "hsl(var(--body-color))",
                }}
              >
                {row.rank}
              </span>
              <div className="flex-grow-1 min-width-0">
                <div
                  className={`font-14 text-truncate ${
                    isCurrent ? "fw-700" : "fw-500"
                  }`}
                  style={
                    isCurrent ? { color: "hsl(var(--main))" } : undefined
                  }
                >
                  {row.name}
                </div>
              </div>
              <div className="text-end">
                <div className="font-11 text-uppercase fw-700 text-body" style={{ letterSpacing: "0.04em" }}>
                  {t("partner.dashboard.leaderboard.deals")}
                </div>
                <div className="fw-700 font-14">{row.deals}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* =====================================================================
 * Tiny building blocks
 * ===================================================================== */

function SectionHeader({ icon, title, subtitle, gradient, align }) {
  return (
    <div
      className={`d-flex ${
        align === "center" ? "flex-column align-items-center text-center" : "align-items-start"
      } gap-3`}
    >
      <span
        className={`d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
          gradient ? "text-white" : ""
        }`}
        style={{
          width: 44,
          height: 44,
          background: gradient
            ? "var(--main-gradient)"
            : "hsl(var(--main) / 0.12)",
          color: gradient ? "#fff" : "hsl(var(--main))",
        }}
        aria-hidden="true"
      >
        <i className={`las ${icon}`} style={{ fontSize: "1.3rem" }} />
      </span>
      <div>
        <h5 className="mb-1 font-18 fw-600">{title}</h5>
        <p className="font-14 text-body mb-0">{subtitle}</p>
      </div>
    </div>
  );
}

function Label({ text }) {
  return (
    <label
      className="font-12 text-uppercase fw-700 text-body mb-1 d-block"
      style={{ letterSpacing: "0.05em" }}
    >
      {text}
    </label>
  );
}

function StatCard({ icon, label, value, hint, tone }) {
  const tones = {
    main: { bg: "hsl(var(--main) / 0.10)", fg: "hsl(var(--main))" },
    info: { bg: "hsl(212 95% 50% / 0.10)", fg: "hsl(212 95% 45%)" },
    success: { bg: "hsl(150 60% 45% / 0.12)", fg: "hsl(150 60% 32%)" },
    warning: { bg: "hsl(38 92% 50% / 0.14)", fg: "hsl(38 92% 38%)" },
  };
  const palette = tones[tone] || tones.main;

  return (
    <div className="col-md-6 col-lg-3">
      <div
        className="rounded-3 p-3 h-100"
        style={{
          backgroundColor: "hsl(var(--section-bg, 210 14% 97%) / 0.6)",
          border: "1px solid hsl(var(--border-color, 220 13% 91%) / 0.6)",
        }}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-3 mb-2"
          style={{
            width: 36,
            height: 36,
            backgroundColor: palette.bg,
            color: palette.fg,
          }}
          aria-hidden="true"
        >
          <i className={`las ${icon}`} style={{ fontSize: "1.2rem" }} />
        </span>
        <div
          className="font-12 text-uppercase fw-700 text-body"
          style={{ letterSpacing: "0.04em" }}
        >
          {label}
        </div>
        <div
          className="font-22 fw-700 mt-1"
          style={{ color: "hsl(var(--heading-color))" }}
        >
          {value}
        </div>
        <div className="font-12 text-body mt-1">{hint}</div>
      </div>
    </div>
  );
}

function CommissionRow({ label, value, accent }) {
  return (
    <li
      className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2"
      style={{
        borderBottom: "1px dashed hsl(var(--border-color, 220 13% 91%) / 0.8)",
      }}
    >
      <span className="font-13 text-body">{label}</span>
      <span
        className={`text-end ${accent ? "font-18 fw-700" : "font-14 fw-600"}`}
        style={
          accent
            ? { color: "hsl(var(--main))" }
            : { color: "hsl(var(--heading-color))" }
        }
      >
        {value}
      </span>
    </li>
  );
}

function ManagerLink({ iconClass, label, value, href, external }) {
  return (
    <li>
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className="d-flex align-items-center gap-3 rounded-3 px-3 py-2 text-decoration-none"
        style={{
          backgroundColor: "hsl(var(--section-bg, 210 14% 97%) / 0.6)",
          border: "1px solid hsl(var(--border-color, 220 13% 91%) / 0.6)",
          color: "hsl(var(--heading-color))",
        }}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{
            width: 32,
            height: 32,
            backgroundColor: "hsl(var(--main) / 0.12)",
            color: "hsl(var(--main))",
          }}
          aria-hidden="true"
        >
          <i className={iconClass} style={{ fontSize: "1rem" }} />
        </span>
        <div className="d-flex flex-column min-width-0">
          <span
            className="font-11 text-uppercase fw-700"
            style={{ opacity: 0.7, letterSpacing: "0.04em" }}
          >
            {label}
          </span>
          <span className="font-14 fw-500 text-truncate">{value}</span>
        </div>
      </a>
    </li>
  );
}

function Th({ children, align }) {
  return (
    <th
      className="font-12 text-uppercase fw-700 text-body py-2"
      style={{
        letterSpacing: "0.04em",
        textAlign: align || "left",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align }) {
  return (
    <td
      className="py-3 font-14"
      style={{
        verticalAlign: "middle",
        textAlign: align || "left",
        color: "hsl(var(--heading-color))",
      }}
    >
      {children}
    </td>
  );
}

export default ApprovedPartnerDashboard;
